import React from 'react';
import { DonutChart } from './Charts';
import { deviceData, readingMethodData, books } from './constants';
import type { Book } from './types';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
interface DevicesTabProps {
  viewMode: string;
  selectedBook: Book;
  setSelectedBook: (book: Book) => void;
  setViewMode: (mode: string) => void;
}
export const DevicesTab = ({
  viewMode,
  selectedBook,
  setSelectedBook,
  setViewMode
}: DevicesTabProps) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  // Referenced by table ternaries below but was never declared.
  const darkMode = dk.isDark;
  return <div className="space-y-6" data-id="element-5917" data-mp="3k0nctth8cy8c">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5918" data-mp="3k1yo0ed0989h">
        {[{
        title: 'Device Types',
        data: deviceData,
        subtitle: viewMode === 'all' ? 'How readers access your books' : `How readers access ${selectedBook.title}`,
        insights: ['Mobile readers spend 25% more time per session', 'E-reader users have the highest completion rate (92%)', 'Desktop readers have the highest drop-off rate']
      }, {
        title: 'Reading Methods',
        data: readingMethodData,
        subtitle: viewMode === 'all' ? 'How readers consume your content' : `How readers consume ${selectedBook.title}`,
        insights: ['In-app readers have 30% higher engagement rates', 'Browser readers tend to read in shorter sessions', 'Downloaded copies have unknown reading patterns']
      }].map(({
        title,
        data,
        subtitle,
        insights
      }) => <div key={title} className={`rounded-lg shadow-sm p-6 border ${dk.card}`} data-id="element-5919" data-mp="12vq3f8f06xh0">
            <h2 className={`text-lg font-medium mb-6 text-center ${dk.textPrimary}`} data-id="element-5920" data-mp="1h3cod63uzzpp">
              {title}
            </h2>
            <DonutChart data={data} title={subtitle} data-id="element-5921" data-mp="1luuknguxtc4c" />
            <div className={`mt-6 pt-6 border-t ${dk.border}`} data-id="element-5922" data-mp="182wmu26of2nh">
              <h3 className={`text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-5923" data-mp="180jtksvgx5v4">
                Key Insights
              </h3>
              <ul className="space-y-1" data-id="element-5924" data-mp="20rbwe971gsrf">
                {insights.map(insight => <li key={insight} className={`flex items-start text-sm ${dk.textSecondary}`} data-id="element-5925" data-mp="20phdt1j8pcqi">
                    <span className={`mr-2 ${rt.textPrimary}`} data-id="element-5926" data-mp="1qscoisu3l15h">•</span>
                    {insight}
                  </li>)}
              </ul>
            </div>
          </div>)}
      </div>

      {/* Reading Session Analysis */}
      <div className={`rounded-lg shadow-sm p-6 border ${dk.card}`} data-id="element-5927" data-mp="kd29zkmcxe38">
        <h2 className={`text-lg font-medium mb-2 ${dk.textPrimary}`} data-id="element-5928" data-mp="ked9v9com1p2">
          Reading Session Analysis
        </h2>
        <p className={`text-sm mb-6 ${dk.textMuted}`} data-id="element-5929" data-mp="cvdvxj7zp2d3">
          When and how long readers engage with your book
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5930" data-mp="lur6kf7unwze">
          <div data-id="element-5931" data-mp="ls4vw6pnx1cy">
            <h3 className={`text-sm font-medium mb-4 ${dk.textSecondary}`} data-id="element-5932" data-mp="gv4qzz4ju4nf">
              Popular Reading Times
            </h3>
            <div className="space-y-3" data-id="element-5933" data-mp="3pa6fte8em2jh">
              {[{
              time: 'Evening (6-10pm)',
              percentage: 42
            }, {
              time: 'Night (10pm-2am)',
              percentage: 28
            }, {
              time: 'Morning (6-10am)',
              percentage: 15
            }, {
              time: 'Afternoon (2-6pm)',
              percentage: 10
            }, {
              time: 'Early morning (2-6am)',
              percentage: 5
            }].map(item => <div key={item.time} className="flex items-center" data-id="element-5934" data-mp="3k6xg8g7uk94a">
                  <div className={`w-32 text-sm ${dk.textSecondary}`} data-id="element-5935" data-mp="3k52tqxgxg8y3">
                    {item.time}
                  </div>
                  <div className={`flex-1 h-6 rounded-full overflow-hidden ${dk.surfaceMuted}`} data-id="element-5936" data-mp="3tx09mhivrp35">
                    <div className={`h-full rounded-full flex items-center px-2 ${rt.bgDark}`} style={{
                  width: `${item.percentage * 2}%`
                }} data-id="element-5937" data-mp="1s646ehoa3uq">
                      <span className="text-xs text-white" data-id="element-5938" data-mp="gqgkmgkttun6">
                        {item.percentage}%
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
          <div data-id="element-5939" data-mp="2iimmxfzv5fop">
            <h3 className={`text-sm font-medium mb-4 ${dk.textSecondary}`} data-id="element-5940" data-mp="2iid3ycd8k8si">
              Average Session Duration
            </h3>
            <div className="space-y-3" data-id="element-5941" data-mp="uw0edqwsmxpk">
              {[{
              device: 'E-Reader',
              duration: 45,
              color: rt.bgDark
            }, {
              device: 'Tablet',
              duration: 32,
              color: 'bg-purple-400'
            }, {
              device: 'Mobile',
              duration: 18,
              color: 'bg-fuchsia-400'
            }, {
              device: 'Desktop',
              duration: 24,
              color: 'bg-pink-400'
            }].map(item => <div key={item.device} className="flex items-center" data-id="element-5942" data-mp="14ljvar0y4hhs">
                  <div className={`w-24 text-sm ${dk.textSecondary}`} data-id="element-5943" data-mp="3ubx1xrxpco5r">
                    {item.device}
                  </div>
                  <div className={`flex-1 h-6 rounded-full overflow-hidden ${dk.surfaceMuted}`} data-id="element-5944" data-mp="3pe3xb3c1s27l">
                    <div className={`${item.color} h-full rounded-full flex items-center px-2`} style={{
                  width: `${item.duration / 45 * 100}%`
                }} data-id="element-5945" data-mp="3vs16p4o9m27r">
                      <span className="text-xs text-white" data-id="element-5946" data-mp="3mtpgv4v838gd">
                        {item.duration} minutes
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
            <h3 className={`text-sm font-medium mt-6 mb-2 ${dk.textSecondary}`} data-id="element-5947" data-mp="2z2k08nhgkc50">
              Weekly Reading Pattern
            </h3>
            <div className="flex items-center justify-between" data-id="element-5948" data-mp="334exngw9q5di">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => <div key={day} className="flex flex-col items-center" data-id="element-5949" data-mp="2u37236l4zin4" data-mpi={index}>
                    <div className={`w-8 rounded-t ${rt.bgDark}`} style={{
                height: `${[30, 35, 40, 38, 45, 60, 48][index]}px`
              }} data-id="element-5950" data-mp="2ah5ll0mp2sm2" />
                    <span className={`text-xs mt-1 ${dk.textMuted}`} data-id="element-5951" data-mp="2kg34no1cptxo">
                      {day}
                    </span>
                  </div>)}
            </div>
          </div>
        </div>
      </div>

      {/* Device Comparison Table */}
      {viewMode === 'all' && <div className={`rounded-lg shadow-sm p-6 border ${dk.card}`} data-id="element-5952" data-mp="2og54rsrfufau">
          <h2 className={`text-lg font-medium mb-4 ${dk.textPrimary}`} data-id="element-5953" data-mp="3362onwic4iu8">
            Device Usage By Book
          </h2>
          <div className="overflow-x-auto" data-id="element-5954" data-mp="2y817jh4gytle">
            <table className={`min-w-full divide-y ${dk.divider}`} data-id="element-5955" data-mp="2y92lpugs7w7c">
              <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`} data-id="element-5956" data-mp="3c70yivfgfkkq">
                <tr data-id="element-5957" data-mp="38677vuamnro5">
                  {['Book', 'Mobile', 'Tablet', 'Desktop', 'E-Reader'].map(h => <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5958" data-mp="igk7k16o8c0i">
                        {h}
                      </th>)}
                </tr>
              </thead>
              <tbody className={`divide-y ${dk.divider}`} data-id="element-5959" data-mp="3vu5fl8xqj7l8">
                {books.map(book => <tr key={book.id} className={`cursor-pointer ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`} onClick={() => {
              setSelectedBook(book);
              setViewMode('individual');
            }} data-id="element-5960" data-mp="4m9rs9cnjs6l">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-5961" data-mp="3d2mc70zstrio">
                      <div className="flex items-center" data-id="element-5962" data-mp="3h3oh9ygecxsk">
                        <img className="h-10 w-8 rounded object-cover mr-4" src={book.coverImage} alt="" data-id="element-5963" data-mp="3h23mc7q67cu6" />
                        <div data-id="element-5964" data-mp="30k8xwtbeih93">
                          <div className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-5965" data-mp="30j7iydr9lam3">
                            {book.title}
                          </div>
                          <div className={`text-sm ${dk.textMuted}`} data-id="element-5966" data-mp="2qmurznn3tav1">
                            {book.author}
                          </div>
                        </div>
                      </div>
                    </td>
                    {['Mobile', 'Tablet', 'Desktop', 'E-Reader'].map(d => <td key={d} className={`px-6 py-4 whitespace-nowrap text-sm ${dk.textPrimary}`} data-id="element-5967" data-mp="2cvzh65t1ojot">
                        {Math.floor(10 + Math.random() * 60)}%
                      </td>)}
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
    </div>;
};